import { Shield, Eye, Download, Trash2, Loader2, UserX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { resolveDashboardMode } from '@/lib/dashboard-mode';
import type { ConsentSettings, FamilyPatient, User } from '@/types/api';
import type { AccountClosureStatus, ConsentDataRequests, DataRequestSummary } from '@/services/api';
import { formatNumericDate } from '@/i18n/intl';
import { ToggleRow } from './ToggleRow';
import { TimezoneField } from '../TimezoneField';
import { PendingDecision } from '@/components/editorial/PendingDecision';
import { digitsOnly, numericInputProps } from '@/components/editorial';

const TREATMENT_PHASES = ['diagnosis', 'active_treatment', 'maintenance', 'survivorship', 'off_treatment', 'other'] as const;

/** The 8-field patient form shape edited in the Patient card. Imported back
 *  into SettingsPage so setPatientForm types line up on both sides. */
export interface PatientFormState {
  name: string;
  date_of_birth: string;
  diagnosis: string;
  treatment_phase: string;
  hospital: string;
  cycle_number: string;
  cycle_started_at: string;
  cycle_length_days: string;
}

interface AccountSectionProps {
  // Profile
  user: User | null;
  companionName: string;
  timezone: string;
  onTimezoneChange: (zone: string) => void;
  profileSaving: boolean;
  onProfileSave: () => void;
  // Patient (parent-only card)
  isParent: boolean;
  patient: FamilyPatient | undefined;
  patientForm: PatientFormState;
  setPatientForm: React.Dispatch<React.SetStateAction<PatientFormState>>;
  patientSaving: boolean;
  onPatientSave: () => void;
  // Privacy & Data
  consent: ConsentSettings;
  saving: boolean;
  onUpdateConsent: (patch: Record<string, unknown>) => void;
  /** #2391: open export / deletion requests, from GET /settings/consent and this visit's clicks. */
  dataRequests: ConsentDataRequests;
  onDataExport: () => void;
  onDataDeletion: () => void;
  /** #2390: the account's closure state from GET /account/close; null when unread. */
  closure: AccountClosureStatus | null;
  // Security (MFA)
  mfaEnabled: boolean;
  mfaToggling: boolean;
  onMfaToggle: (checked: boolean) => void;
  showEnrollCodeInput: boolean;
  setShowEnrollCodeInput: (v: boolean) => void;
  mfaEnrollCode: string;
  setMfaEnrollCode: (v: string) => void;
  onMfaEnrollVerify: () => void;
  showPasswordInput: boolean;
  setShowPasswordInput: (v: boolean) => void;
  password: string;
  setPassword: (v: string) => void;
  onMfaDisable: () => void;
}

export function AccountSection({
  user,
  companionName,
  timezone,
  onTimezoneChange,
  profileSaving,
  onProfileSave,
  isParent,
  patient,
  patientForm,
  setPatientForm,
  patientSaving,
  onPatientSave,
  consent,
  saving,
  onUpdateConsent,
  dataRequests,
  onDataExport,
  onDataDeletion,
  closure,
  mfaEnabled,
  mfaToggling,
  onMfaToggle,
  showEnrollCodeInput,
  setShowEnrollCodeInput,
  mfaEnrollCode,
  setMfaEnrollCode,
  onMfaEnrollVerify,
  showPasswordInput,
  setShowPasswordInput,
  password,
  setPassword,
  onMfaDisable,
}: AccountSectionProps) {
  // The memorial register on the Patient card (#685 / #693).
  //
  // Derived in the module from the phase this component is already handed,
  // rather than threaded in as a prop, so a caller cannot forget it. Two
  // things here are treatment-era, and one of them was the loudest surviving
  // instance in the platform: `patient.day_in_cycle` is recomputed from
  // `cycle_started_at` on every request (backend/src/routes/families.js:42)
  // with no upper bound, so a bereaved family's settings page read "Current:
  // day 214 of 21" — their dead child's chemo cycle, still counting up, once a
  // day, forever. The "Treatment phase" select is the other: its option list
  // has no `bereaved` entry, so the control rendered its placeholder as though
  // the phase were unset, on the one record where it is most certainly set.
  //
  // Fail-safe direction matches `resolveDashboardMode` everywhere else: an
  // unresolved phase is the treatment register. Here that costs nothing — a
  // missing `patient` also means an empty form.
  const isMemorial = resolveDashboardMode(patient?.treatment_phase) === 'memorial';
  const { t } = useTranslation(['settings', 'common']);
  const companion = companionName;
  const exportRequest = dataRequests.export ?? null;
  const deletionRequest = dataRequests.deletion ?? null;
  // A pending request's line: its date when the server gave one, otherwise
  // the row's own description (a request made this visit has no date yet).
  const requestLine = (r: DataRequestSummary | null, fallback: string) =>
    r?.requested_at ? t('account.privacy.requestedOn', { date: formatNumericDate(r.requested_at) }) : fallback;
  return (
    <>
      {/* ── Profile ─────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('account.profile.title')}</CardTitle>
          <CardDescription>{t('account.profile.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <TimezoneField value={timezone} onChange={onTimezoneChange} />
          <div className="flex justify-end">
            <Button onClick={onProfileSave} disabled={profileSaving} size="sm">
              {profileSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('common:actions.save')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Patient ─────────────────────────────────────── */}
      {user?.role === 'parent' && (
      <Card>
        <CardHeader>
          <CardTitle>{t('account.patient.title')}</CardTitle>
          <CardDescription>
            {isMemorial
              ? t('account.patient.descriptionMemorial', { companion })
              : t('account.patient.description', { companion })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="patient-name">{t('account.patient.childName')}</Label>
              <Input
                id="patient-name"
                value={patientForm.name}
                onChange={(e) => setPatientForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="patient-dob">{t('account.patient.dateOfBirth')}</Label>
              <Input
                id="patient-dob"
                type="date"
                value={patientForm.date_of_birth}
                onChange={(e) => setPatientForm((f) => ({ ...f, date_of_birth: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="patient-diagnosis">{t('account.patient.diagnosis')}</Label>
              <Input
                id="patient-diagnosis"
                placeholder={t('account.patient.diagnosisPlaceholder')}
                value={patientForm.diagnosis}
                onChange={(e) => setPatientForm((f) => ({ ...f, diagnosis: e.target.value }))}
              />
            </div>
            {/* Treatment phase — withheld in the memorial register. The option
                list below has no `bereaved` entry (staff set that phase, from
                the TRFer profile), so for a bereaved family this control
                rendered "Select phase" as though the field were blank and
                offered five live-treatment values in its place. */}
            {!isMemorial && (
            <div className="space-y-2">
              <Label htmlFor="patient-phase">{t('account.patient.treatmentPhase')}</Label>
              <Select
                value={patientForm.treatment_phase}
                onValueChange={(v) => setPatientForm((f) => ({ ...f, treatment_phase: v }))}
              >
                <SelectTrigger id="patient-phase">
                  <SelectValue placeholder={t('account.patient.phasePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {TREATMENT_PHASES.map((phase) => (
                    <SelectItem key={phase} value={phase}>{t(`account.patient.phases.${phase}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="patient-hospital">{t('account.patient.hospital')}</Label>
            <Input
              id="patient-hospital"
              value={patientForm.hospital}
              onChange={(e) => setPatientForm((f) => ({ ...f, hospital: e.target.value }))}
            />
          </div>

          {/* Treatment cycle — withheld whole in the memorial register. Every
              field here is an instruction to keep a live chemo cycle up to
              date, and the "Current: day N" line below is computed fresh from
              `cycle_started_at` on every request, so it counted upward past
              the child's death indefinitely. */}
          {!isMemorial && (
          <div className="border-t pt-4 space-y-3">
            <div>
              <Label className="text-body font-medium">{t('account.patient.cycle.title')}</Label>
              <p className="text-caption text-stevens-ink-3 mt-0.5">
                {t('account.patient.cycle.helper', { companion })}{' '}
                <PendingDecision id="treatment-cycle-field-order" side="bottom" />
              </p>
            </div>
            {/* #2436: three equal columns left the date input 73 px wide at
                390 px ("mm/dd/"), and a label that wraps in one language
                pushed its input out of line. Phone: number and length side
                by side, the start date full width below; sm+: three columns.
                items-end keeps the inputs on one line when a label wraps. */}
            <div className="grid grid-cols-2 items-end gap-3 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="patient-cycle-num" className="text-caption">{t('account.patient.cycle.number')}</Label>
                <Input
                  id="patient-cycle-num"
                  {...numericInputProps({ max: 99 })}
                  placeholder="3"
                  value={patientForm.cycle_number}
                  onChange={(e) => setPatientForm((f) => ({ ...f, cycle_number: digitsOnly(e.target.value) }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="patient-cycle-len" className="text-caption">{t('account.patient.cycle.length')}</Label>
                <Input
                  id="patient-cycle-len"
                  {...numericInputProps({ max: 60 })}
                  placeholder="21"
                  value={patientForm.cycle_length_days}
                  onChange={(e) => setPatientForm((f) => ({ ...f, cycle_length_days: digitsOnly(e.target.value) }))}
                />
              </div>
              <div className="col-span-2 space-y-2 sm:col-span-1">
                <Label htmlFor="patient-cycle-start" className="text-caption">{t('account.patient.cycle.started')}</Label>
                <Input
                  id="patient-cycle-start"
                  type="date"
                  value={patientForm.cycle_started_at}
                  onChange={(e) => setPatientForm((f) => ({ ...f, cycle_started_at: e.target.value }))}
                />
              </div>
            </div>
            {patient?.day_in_cycle != null && patientForm.cycle_started_at && (
              <p className="text-caption text-stevens-ink-3">
                {patient.cycle_length_days
                  ? t('account.patient.cycle.currentOf', { day: patient.day_in_cycle, length: patient.cycle_length_days })
                  : t('account.patient.cycle.current', { day: patient.day_in_cycle })}
              </p>
            )}
          </div>
          )}

          <div className="flex justify-end">
            <Button onClick={onPatientSave} disabled={patientSaving} size="sm">
              {patientSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('common:actions.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* ── Privacy & Data ──────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            <CardTitle>{t('account.privacy.title')}</CardTitle>
          </div>
          <CardDescription>{t('account.privacy.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* "Circle sharing default" removed (#642, D-7): it persisted
              User.consent.circle_sharing_default, which no code path ever
              read to gate visibility — circle members never log in, so
              there is nothing to enforce it against. */}
          {isParent && (
            <ToggleRow
              label={t('account.privacy.conversationAccess', { companion })}
              description={t('account.privacy.conversationAccessDescription', { companion })}
              checked={consent.trfer_conversation_access}
              disabled={saving}
              onCheckedChange={(v) => onUpdateConsent({ trfer_conversation_access: v })}
            />
          )}

          {/* #2421: at 390 px the 188 px "Request export" button squeezed the
              title and description into a 57 px column. Each row stacks on a
              phone and sits side by side from sm. */}
          <div className="border-t pt-4 space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <Label className="text-body font-medium">{t('account.privacy.exportTitle')}</Label>
                <p className="text-caption text-stevens-ink-3">
                  {requestLine(exportRequest, t('account.privacy.exportDescription'))}
                  {/* #2391: how a pending request reads, marked once on the first pending row. */}
                  {exportRequest && <>{' '}<PendingDecision id="data-request-pending-shape" side="bottom" /></>}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 self-start sm:self-auto"
                onClick={onDataExport}
                disabled={!!exportRequest}
              >
                <Download className="h-4 w-4 mr-2" />
                {exportRequest ? t('account.privacy.exportRequested') : t('account.privacy.exportAction')}
              </Button>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <Label className="text-body font-medium">{t('account.privacy.deleteTitle')}</Label>
                <p className="text-caption text-stevens-ink-3">
                  {requestLine(deletionRequest, t('account.privacy.deleteDescription'))}
                  {deletionRequest && !exportRequest && <>{' '}<PendingDecision id="data-request-pending-shape" side="bottom" /></>}
                </p>
              </div>
              {deletionRequest ? (
                <Button variant="outline" size="sm" className="shrink-0 self-start sm:self-auto" disabled>
                  <Trash2 className="h-4 w-4 me-2" />
                  {t('account.privacy.deleteRequested')}
                </Button>
              ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" className="shrink-0 self-start sm:self-auto">
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('account.privacy.deleteAction')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('account.privacy.deleteDialogTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('account.privacy.deleteDialogDescription')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
                    <AlertDialogAction onClick={onDataDeletion}>
                      {t('account.privacy.deleteConfirm')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              )}
            </div>

            {/* #2390: the closure row. A scheduled closure used to be visible
                only on the page that scheduled it, and only until the page
                was left; this row reads it back and links to the page that
                cancels it (D-2390). Without a status (null — the read failed
                or is pending) the row is still the way in to closing. */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-body font-medium">
                  {t('account.privacy.closeTitle')}{' '}
                  {/* #2390: who sees this row is a default; D-2390 rules only the in-app cancel. */}
                  <PendingDecision id="account-closure-row-roles" side="bottom" />
                </p>
                <p className="text-caption text-stevens-ink-3">
                  {closure?.closure_scheduled_at
                    ? t('account.privacy.closeScheduledDescription', { date: formatNumericDate(closure.closure_scheduled_at) })
                    : t('account.privacy.closeDescription', { days: closure?.grace_days ?? 30 })}
                </p>
              </div>
              <Button asChild variant="outline" size="sm" className="shrink-0 self-start sm:self-auto">
                <Link to="/account/close">
                  <UserX className="h-4 w-4 me-2" aria-hidden />
                  {closure?.closure_scheduled_at ? t('account.privacy.closeReview') : t('account.privacy.closeAction')}
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Security ────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <CardTitle>{t('account.security.title')}</CardTitle>
          </div>
          <CardDescription>{t('account.security.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* #2422: the switch is named by its label and described by the
              state line under it. */}
          <div className="flex items-center justify-between">
            <div className="min-w-0 pe-4">
              <Label htmlFor="mfa-switch" className="text-body font-medium">{t('account.security.mfaTitle')}</Label>
              <p id="mfa-switch-description" className="text-caption text-stevens-ink-3 mt-0.5">
                {mfaEnabled ? t('account.security.mfaOn') : t('account.security.mfaOff')}
              </p>
            </div>
            <Switch
              id="mfa-switch"
              aria-describedby="mfa-switch-description"
              checked={mfaEnabled}
              onCheckedChange={onMfaToggle}
              disabled={mfaToggling}
            />
          </div>

          {showEnrollCodeInput && (
            <div className="space-y-3 pt-2 border-t">
              <Label htmlFor="mfa-enroll-code">{t('account.security.enterCode')}</Label>
              <Input
                id="mfa-enroll-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={mfaEnrollCode}
                onChange={(e) => setMfaEnrollCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoFocus
                className="font-mono text-center tracking-widest"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={onMfaEnrollVerify} disabled={mfaEnrollCode.length !== 6 || mfaToggling}>
                  {mfaToggling ? t('account.security.verifying') : t('account.security.verify')}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setShowEnrollCodeInput(false); setMfaEnrollCode(''); }}>
                  {t('common:actions.cancel')}
                </Button>
              </div>
            </div>
          )}

          {showPasswordInput && (
            <div className="space-y-3 pt-2 border-t">
              <Label htmlFor="confirm-password">{t('account.security.confirmPassword')}</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder={t('account.security.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div className="flex gap-2">
                <Button onClick={onMfaDisable} disabled={!password || mfaToggling} variant="destructive" size="sm">
                  {t('account.security.turnOff')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowPasswordInput(false); setPassword(''); }}
                >
                  {t('common:actions.cancel')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
